// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Version Comparison Component
// Compare all Viktor versions with best performer badge
// ═══════════════════════════════════════════════════════════════════════════════

import { Star, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  formatUiDate,
  formatUiNumber,
  formatUiPercent,
} from '@/lib/ui-format';

interface Version {
  id: string;
  displayName: string;
  version: string;
  isActive: boolean;
  isBestPerformer: boolean;
  conversionRate: number;
  totalSessions: number;
  createdAt: string;
}

interface VersionComparisonProps {
  versions: Version[];
  currentVersionId: string;
  onVersionSelect?: (versionId: string) => void;
}

export function VersionComparison({
  versions,
  currentVersionId,
  onVersionSelect,
}: VersionComparisonProps) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Version History</CardTitle>
        <p className="text-sm text-muted-foreground">
          Compare Viktor performance across versions
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {versions.map((version) => {
            const isSelected = version.id === currentVersionId;

            return (
              <button
                key={version.id}
                type="button"
                onClick={() => onVersionSelect?.(version.id)}
                className={cn(
                  'min-h-11 w-full rounded-lg border p-4 text-left transition-all',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-surface-hover',
                  !version.isActive && 'opacity-60'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-foreground">
                        {version.displayName}
                      </h3>
                      
                      {version.isBestPerformer && (
                        <Star className="w-4 h-4 text-status-warning fill-status-warning" />
                      )}
                      
                      {version.isActive && (
                        <Badge variant="outline" className="text-xs">
                          Active
                        </Badge>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {formatUiDate(version.createdAt)}
                    </p>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold text-foreground">
                        {formatUiPercent(version.conversionRate / 100, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                      </span>
                      {version.conversionRate > 0 && (
                        <TrendingUp className="w-4 h-4 text-status-success" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatUiNumber(version.totalSessions)} sessions
                    </p>
                  </div>
                </div>
              </button>
            );
          })}

          {versions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No versions created yet
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
